import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Check, Shield, X } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const ROLES = [
  { key: "super_admin", label: "Super Admin" },
  { key: "organization.owner", label: "Owner" },
  { key: "organization.admin", label: "Admin" },
  { key: "organization.member", label: "Member" },
] as const;

function formatAction(action: string): string {
  return action
    .replace(/^can/, "")
    .replace(/([A-Z])/g, " $1")
    .trim();
}

function formatResource(resource: string): string {
  return resource.charAt(0).toUpperCase() + resource.slice(1);
}

function PermissionsMatrix() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: api.admin.permissions,
  });

  if (isLoading) return <LoadingIndicator />;
  if (isError || !data) return <ErrorComponent error={error ?? "Failed to load permissions"} />;

  const { policies } = data;

  return (
    <div className="space-y-8">
      {Object.entries(policies).map(([resource, actions]) => (
        <section key={resource} className="space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="size-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">{formatResource(resource)}</h3>
          </div>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Action</TableHead>
                  {ROLES.map((role) => (
                    <TableHead key={role.key} className="text-center">
                      <Badge
                        variant={role.key === "super_admin" ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {role.label}
                      </Badge>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(actions).map(([action, allowedRoles]) => (
                  <TableRow key={action}>
                    <TableCell className="font-medium">{formatAction(action)}</TableCell>
                    {ROLES.map((role) => {
                      const allowed = (allowedRoles as readonly string[]).includes(role.key);
                      return (
                        <TableCell key={role.key} className="text-center">
                          {allowed ? (
                            <Check className="inline-block size-4 text-green-600" />
                          ) : (
                            <X className="inline-block size-4 text-muted-foreground/40" />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ))}
    </div>
  );
}

export function AdminPermissionsPage() {
  useDocumentTitle("Admin: Permissions");
  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Permissions"
        subtitle="Full permission matrix showing which roles can perform each action."
      />
      <PermissionsMatrix />
    </div>
  );
}
